import { AxiosRequestConfig } from 'axios';

export interface CacheConfig {
  ttl?: number; // Time to live in milliseconds
  cacheKey?: string; // Custom cache key
  bypassCache?: boolean; // Force bypass cache
  invalidatePattern?: string; // Pattern to invalidate related cache entries
}

export interface CacheEntry {
  data: any;
  timestamp: number;
  ttl: number;
  etag?: string;
  headers?: Record<string, string>;
}

export interface CacheStats {
  hits: number;
  misses: number;
  hitRate: number;
  size: number;
  entries: number;
}

class CacheService {
  private cache: Map<string, CacheEntry> = new Map();
  private stats = {
    hits: 0,
    misses: 0,
  };
  private defaultTTL = 10 * 60 * 1000; // 10 minutes default
  private maxCacheSize = 100 * 1024 * 1024; // 100MB max cache size
  private currentCacheSize = 0;

  // TTL configurations for different endpoint patterns
  private ttlConfigs: Record<string, number> = {
    // Static data - longer cache
    '/users': 30 * 60 * 1000, // 30 minutes - users don't change often
    '/teams': 30 * 60 * 1000, // 30 minutes
    '/labels': 60 * 60 * 1000, // 60 minutes
    '/shops': 30 * 60 * 1000, // 30 minutes
    
    // Dynamic data - moderate cache
    '/projects': 10 * 60 * 1000, // 10 minutes - projects don't change frequently
    '/tasks': 5 * 60 * 1000, // 5 minutes - tasks change more often
    '/notifications': 60 * 1000, // 1 minute
    '/time-tracking/active': 30 * 1000, // 30 seconds
    '/time-tracking/today': 5 * 60 * 1000, // 5 minutes for today's entries
    '/time-tracking': 10 * 60 * 1000, // 10 minutes for general time entries
    
    // Real-time data - short cache
    '/users/online': 30 * 1000, // 30 seconds - still fresh enough
    '/crm/opportunities/metrics': 60 * 1000, // 1 minute
    
    // Reports and analytics - longer cache
    '/sales/dashboard': 15 * 60 * 1000, // 15 minutes
    '/time-tracking/monthly-hours': 15 * 60 * 1000, // 15 minutes
    '/requests/stats': 10 * 60 * 1000, // 10 minutes
    '/sourcing': 10 * 60 * 1000, // 10 minutes
  };

  constructor() {
    // Cleanup expired entries every minute
    setInterval(() => this.cleanupExpiredEntries(), 60 * 1000);
    
    // Load cache stats from localStorage if available
    this.loadStats();
  }

  /**
   * Generate cache key from request config
   */
  generateCacheKey(config: AxiosRequestConfig): string {
    const { method = 'GET', url = '', params, data } = config;
    
    // Only cache GET requests by default
    if (method.toUpperCase() !== 'GET') {
      return '';
    }

    // Create a stable key from URL and parameters
    const paramString = params ? JSON.stringify(this.sortObject(params)) : '';
    const dataString = data ? JSON.stringify(this.sortObject(data)) : '';
    
    return `${method}:${url}:${paramString}:${dataString}`;
  }

  /**
   * Sort object keys for consistent cache key generation
   */
  private sortObject(obj: any): any {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.sortObject(item));
    }

    return Object.keys(obj)
      .sort()
      .reduce((result: any, key: string) => {
        result[key] = this.sortObject(obj[key]);
        return result;
      }, {});
  }

  /**
   * Get TTL for a specific endpoint
   */
  getTTLForEndpoint(url: string): number {
    // Check exact matches first
    for (const [pattern, ttl] of Object.entries(this.ttlConfigs)) {
      if (url.includes(pattern)) {
        return ttl;
      }
    }
    
    return this.defaultTTL;
  }

  /**
   * Get cached data if valid
   */
  get(key: string): CacheEntry | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.stats.misses++;
      return null;
    }

    // Check if entry is expired
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      this.updateCacheSize(-this.getEntrySize(entry));
      this.stats.misses++;
      return null;
    }

    this.stats.hits++;
    return entry;
  }

  /**
   * Set cache entry
   */
  set(key: string, data: any, config?: CacheConfig): void {
    // Don't cache if bypassed
    if (config?.bypassCache) {
      return;
    }

    const ttl = config?.ttl || this.getTTLForEndpoint(key);
    
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      ttl,
      headers: data.headers,
      etag: data.headers?.etag,
    };

    // Check cache size limit
    const entrySize = this.getEntrySize(entry);
    if (this.currentCacheSize + entrySize > this.maxCacheSize) {
      this.evictLRU();
    }

    // Remove old entry if exists
    const oldEntry = this.cache.get(key);
    if (oldEntry) {
      this.updateCacheSize(-this.getEntrySize(oldEntry));
    }

    this.cache.set(key, entry);
    this.updateCacheSize(entrySize);
    
    this.saveStats();
  }

  /**
   * Invalidate cache entries
   */
  invalidate(pattern?: string): void {
    if (!pattern) {
      // Clear all cache
      this.cache.clear();
      this.currentCacheSize = 0;
      return;
    }

    // Invalidate entries matching pattern
    const keysToDelete: string[] = [];
    
    Array.from(this.cache.entries()).forEach(([key, entry]) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
        this.updateCacheSize(-this.getEntrySize(entry));
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Invalidate cache based on HTTP method and URL
   */
  invalidateByOperation(method: string, url: string): void {
    const upperMethod = method.toUpperCase();
    
    // Only invalidate on mutating operations
    if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(upperMethod)) {
      return;
    }

    // Extract resource from URL
    const urlParts = url.split('/');
    const resource = urlParts[urlParts.length - 2] || urlParts[urlParts.length - 1];
    
    // Invalidate related cache entries
    const patterns: string[] = [];
    
    switch (resource) {
      case 'users':
        patterns.push('/users', '/teams', '/projects');
        break;
      case 'projects':
        patterns.push('/projects', '/tasks', '/time-tracking');
        break;
      case 'tasks':
        patterns.push('/tasks', '/projects', '/users');
        break;
      case 'teams':
        patterns.push('/teams', '/users', '/projects');
        break;
      case 'time-tracking':
        patterns.push('/time-tracking', '/users', '/projects');
        break;
      case 'sales':
        patterns.push('/sales', '/crm');
        break;
      case 'crm':
        patterns.push('/crm', '/sales');
        break;
      default:
        patterns.push(`/${resource}`);
    }

    patterns.forEach(pattern => this.invalidate(pattern));
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? this.stats.hits / total : 0;
    
    return {
      hits: this.stats.hits,
      misses: this.stats.misses,
      hitRate,
      size: this.currentCacheSize,
      entries: this.cache.size,
    };
  }

  /**
   * Clean up expired entries
   */
  private cleanupExpiredEntries(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    Array.from(this.cache.entries()).forEach(([key, entry]) => {
      if (now - entry.timestamp > entry.ttl) {
        keysToDelete.push(key);
        this.updateCacheSize(-this.getEntrySize(entry));
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Evict least recently used entries when cache is full
   */
  private evictLRU(): void {
    // Sort entries by timestamp (oldest first)
    const entries = Array.from(this.cache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    // Remove oldest entries until we have enough space
    const targetSize = this.maxCacheSize * 0.8; // Free up to 80% capacity
    
    for (const [key, entry] of entries) {
      if (this.currentCacheSize <= targetSize) {
        break;
      }
      
      this.cache.delete(key);
      this.updateCacheSize(-this.getEntrySize(entry));
    }
  }

  /**
   * Calculate entry size in bytes
   */
  private getEntrySize(entry: CacheEntry): number {
    // Rough estimation of entry size
    return JSON.stringify(entry).length * 2; // 2 bytes per character
  }

  /**
   * Update current cache size
   */
  private updateCacheSize(delta: number): void {
    this.currentCacheSize = Math.max(0, this.currentCacheSize + delta);
  }

  /**
   * Save stats to localStorage
   */
  private saveStats(): void {
    try {
      localStorage.setItem('api-cache-stats', JSON.stringify(this.stats));
    } catch (error) {
      console.warn('Failed to save cache stats:', error);
    }
  }

  /**
   * Load stats from localStorage
   */
  private loadStats(): void {
    try {
      const saved = localStorage.getItem('api-cache-stats');
      if (saved) {
        const stats = JSON.parse(saved);
        this.stats.hits = stats.hits || 0;
        this.stats.misses = stats.misses || 0;
      }
    } catch (error) {
      console.warn('Failed to load cache stats:', error);
    }
  }

  /**
   * Generate ETag for response data
   */
  generateETag(data: any): string {
    // Simple hash function for ETag generation
    const str = JSON.stringify(data);
    let hash = 0;
    
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return `"${Math.abs(hash).toString(36)}"`;
  }

  /**
   * Check if data has changed based on ETag
   */
  hasDataChanged(etag1?: string, etag2?: string): boolean {
    if (!etag1 || !etag2) {
      return true;
    }
    
    return etag1 !== etag2;
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
    this.currentCacheSize = 0;
    this.stats = { hits: 0, misses: 0 };
    this.saveStats();
  }

  /**
   * Export cache data for debugging
   */
  exportCache(): Record<string, any> {
    const data: Record<string, any> = {};
    
    Array.from(this.cache.entries()).forEach(([key, entry]) => {
      data[key] = {
        timestamp: new Date(entry.timestamp).toISOString(),
        ttl: entry.ttl,
        expiresAt: new Date(entry.timestamp + entry.ttl).toISOString(),
        size: this.getEntrySize(entry),
        hasETag: !!entry.etag,
      };
    });
    
    return {
      stats: this.getStats(),
      entries: data,
      config: {
        defaultTTL: this.defaultTTL,
        maxCacheSize: this.maxCacheSize,
        ttlConfigs: this.ttlConfigs,
      },
    };
  }
}

export const cacheService = new CacheService();